import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  bannersApi,
  Banner,
  CreateBannerDto,
  UpdateBannerDto,
} from '../../../api/banners';
import { useConfirm } from '../../../components/shared/ConfirmDialog';
import { UseBannersReturn, BannerFormErrors } from '../types';
import { fromTashkentInput, toTashkentInput } from '@/lib/datetime';
import { getErrorMessage } from '@/lib/errors';

const initialFormData: CreateBannerDto = {
  name: '',
  title: { uz: '', ru: '', kiril: '' },
  subtitle: { uz: '', ru: '', kiril: '' },
  preset: 'gold',
  icon: 'gift',
  custom_style: null,
  action_type: 'payment',
  action_payload: null,
  audience: 'free',
  platform: 'all',
  priority: 0,
  is_active: true,
  starts_at: null,
  ends_at: null,
};

/** Пустые строки локализации не отправляем — на сервере поле остаётся пустым. */
function cleanLocalized(value?: { uz?: string; ru?: string; kiril?: string }) {
  if (!value) return {};
  const result: Record<string, string> = {};
  (['uz', 'ru', 'kiril'] as const).forEach((lang) => {
    const text = value[lang]?.trim();
    if (text) result[lang] = text;
  });
  return result;
}

export function useBanners(): UseBannersReturn {
  const confirm = useConfirm();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [formData, setFormData] = useState<CreateBannerDto>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<BannerFormErrors>({});

  const validateForm = (): BannerFormErrors => {
    const next: BannerFormErrors = {};

    if (!formData.name.trim()) {
      next.name = 'Укажите название — оно видно только в админке';
    }

    // Заголовок обязателен хотя бы на одном языке: без него баннер пустой.
    const title = cleanLocalized(formData.title);
    if (Object.keys(title).length === 0) {
      next.title = 'Заполните заголовок хотя бы на одном языке';
    }

    if (formData.action_type === 'external_url') {
      const url = formData.action_payload?.trim();
      if (!url) {
        next.action_payload = 'Укажите ссылку';
      } else if (!/^https?:\/\//i.test(url)) {
        next.action_payload = 'Ссылка должна начинаться с http:// или https://';
      }
    }

    if (
      formData.starts_at &&
      formData.ends_at &&
      new Date(formData.starts_at) > new Date(formData.ends_at)
    ) {
      next.ends_at = 'Дата начала не может быть позже даты окончания';
    }

    return next;
  };

  const clearFieldError = (field: keyof BannerFormErrors) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const fetchBanners = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setBanners(await bannersApi.getAll());
    } catch (error: unknown) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить баннеры'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const buildPayload = (): CreateBannerDto => ({
    name: formData.name.trim(),
    title: cleanLocalized(formData.title),
    subtitle: cleanLocalized(formData.subtitle),
    preset: formData.preset,
    custom_style: formData.custom_style ?? null,
    icon: formData.icon,
    action_type: formData.action_type,
    // Payload имеет смысл только для внешней ссылки — иначе чистим,
    // чтобы в базе не оставался мусор от смены типа действия.
    action_payload:
      formData.action_type === 'external_url'
        ? formData.action_payload?.trim() || null
        : null,
    audience: formData.audience,
    platform: formData.platform,
    priority: Number(formData.priority) || 0,
    is_active: formData.is_active,
    // В форме — время Ташкента из datetime-local, на бэкенд уходит ISO-момент.
    starts_at: fromTashkentInput(formData.starts_at),
    ends_at: fromTashkentInput(formData.ends_at),
  });

  const openCreateDialog = () => {
    setFormData(initialFormData);
    setErrors({});
    setIsCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    if (isSubmitting) return;
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      setIsSubmitting(true);
      await bannersApi.create(buildPayload());
      toast.success('Баннер создан');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchBanners();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось создать баннер'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (banner: Banner) => {
    setEditingBanner(banner);
    setErrors({});
    setFormData({
      name: banner.name,
      title: banner.title ?? {},
      subtitle: banner.subtitle ?? {},
      preset: banner.preset,
      custom_style: banner.custom_style,
      icon: banner.icon,
      action_type: banner.action_type,
      action_payload: banner.action_payload,
      audience: banner.audience,
      platform: banner.platform,
      priority: banner.priority,
      is_active: banner.is_active,
      // datetime-local показывает время Ташкента (без зоны).
      starts_at: toTashkentInput(banner.starts_at) || null,
      ends_at: toTashkentInput(banner.ends_at) || null,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingBanner || isSubmitting) return;
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      setIsSubmitting(true);
      await bannersApi.update(editingBanner.id, buildPayload() as UpdateBannerDto);
      toast.success('Баннер обновлён');
      setIsEditDialogOpen(false);
      setEditingBanner(null);
      fetchBanners();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось обновить баннер'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const banner = banners.find((b) => b.id === id);
    const confirmed = await confirm({
      title: 'Удалить баннер?',
      description: banner?.impressions_count
        ? `Статистика показов будет удалена вместе с баннером (${banner.impressions_count} показов).`
        : 'Действие нельзя отменить.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await bannersApi.delete(id);
      toast.success('Баннер удалён');
      fetchBanners();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось удалить баннер'));
    }
  };

  /** Быстрое вкл/выкл прямо из таблицы — акцию надо гасить одним кликом. */
  const handleToggleActive = async (banner: Banner) => {
    try {
      await bannersApi.update(banner.id, { is_active: !banner.is_active });
      setBanners((prev) =>
        prev.map((b) =>
          b.id === banner.id ? { ...b, is_active: !banner.is_active } : b,
        ),
      );
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось изменить статус'));
    }
  };

  return {
    loadError,
    retryLoad: fetchBanners,
    banners,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingBanner,
    formData,
    isSubmitting,
    errors,
    clearFieldError,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    openCreateDialog,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  };
}
